import { isCompanyRegistrationApprovalEnabled } from '../config/featureFlags.ts';
/** Campos editáveis do cadastro. Credenciais e estados de envio nunca são rascunho. */
export const registrationTextFields = [
  'fullName', 'email', 'clinicName', 'clinicCnpj', 'adminPhone',
  'clinicDisplayName', 'clinicLocation', 'clinicAddress', 'clinicWhatsApp',
  'clinicLandline', 'clinicInstagram', 'clinicFacebook', 'clinicSite',
  'googleMapsLink', 'clinicHistory', 'financialRedirectNumber',
  'regularRedirectNumber', 'maintenanceRedirectNumber', 'activePatientsRedirectNumber',
  'assistantName', 'assistantRole', 'assistantFormality', 'assistantTone',
  'assistantLanguage', 'technicalResponsible', 'healthInsurancePlans',
  'paymentMethods', 'installmentConditions', 'evaluationPrice', 'treatmentPrices',
] as const;

export const registrationDays = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

export interface RegistrationDayDraft {
  open: boolean;
  morningEnabled: boolean;
  morningStart: string;
  morningEnd: string;
  afternoonEnabled: boolean;
  afternoonStart: string;
  afternoonEnd: string;
}

export type RegistrationDraftData = Partial<
  Record<typeof registrationTextFields[number], string> &
  Record<typeof registrationDays[number], RegistrationDayDraft> & {
    currentStep: 1 | 2 | 3 | 4 | 5 | 6;
    personType: 'pj' | 'pf';
    clinicLogo: string | null;
    treatments: { treatmentTitle: string; description: string }[];
    consultationDuration: number;
    acceptsHealthInsurance: boolean;
    pricesUnderEvaluation: boolean;
  }
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Restaura apenas a estrutura conhecida; validação de negócio ocorre ao finalizar. */
export function parseRegistrationDraft(value: unknown): RegistrationDraftData {
  if (!isRecord(value)) return {};
  const parsed: RegistrationDraftData = {};
  for (const key of registrationTextFields) {
    if (typeof value[key] === 'string') parsed[key] = value[key] as string;
  }
  if (Number.isInteger(value.currentStep) && Number(value.currentStep) >= 1 && Number(value.currentStep) <= 6) {
    parsed.currentStep = value.currentStep as RegistrationDraftData['currentStep'];
  }
  if (value.personType === 'pj') parsed.personType = 'pj';
  if (value.personType === 'pf' && !isCompanyRegistrationApprovalEnabled()) parsed.personType = 'pf';
  if (value.personType === 'pf' && isCompanyRegistrationApprovalEnabled()) {
    // Rascunhos antigos preservam o conteúdo editável, sem restaurar CPF como CNPJ.
    parsed.personType = 'pj';
    parsed.clinicCnpj = '';
    parsed.currentStep = 1;
  }
  if (value.clinicLogo === null || (typeof value.clinicLogo === 'string' && /^data:image\/[a-zA-Z0-9.+-]+;base64,/.test(value.clinicLogo))) {
    parsed.clinicLogo = value.clinicLogo as string | null;
  }
  for (const key of ['acceptsHealthInsurance', 'pricesUnderEvaluation'] as const) {
    if (typeof value[key] === 'boolean') parsed[key] = value[key] as boolean;
  }
  if (typeof value.consultationDuration === 'number' && Number.isFinite(value.consultationDuration)) {
    parsed.consultationDuration = value.consultationDuration;
  }
  if (Array.isArray(value.treatments) && value.treatments.every(t =>
    isRecord(t) && typeof t.treatmentTitle === 'string' && typeof t.description === 'string'
  )) {
    parsed.treatments = value.treatments.map(t => ({ treatmentTitle: t.treatmentTitle, description: t.description }));
  }
  for (const day of registrationDays) {
    const d = value[day];
    if (!isRecord(d)) continue;
    if (['open', 'morningEnabled', 'afternoonEnabled'].every(k => typeof d[k] === 'boolean') &&
        ['morningStart', 'morningEnd', 'afternoonStart', 'afternoonEnd'].every(k => typeof d[k] === 'string')) {
      parsed[day] = {
        open: d.open as boolean, morningEnabled: d.morningEnabled as boolean,
        morningStart: d.morningStart as string, morningEnd: d.morningEnd as string,
        afternoonEnabled: d.afternoonEnabled as boolean,
        afternoonStart: d.afternoonStart as string, afternoonEnd: d.afternoonEnd as string,
      };
    }
  }
  return parsed;
}
